import {useState, useEffect, useCallback} from 'react';
import axios from "@common/axios";

const useSavedReport = ({ savedReportId, setTableData, setClassName, setClassAverages }) => {
    const [reportName, setReportName] = useState(null);
    const [reportDescription, setReportDescription] = useState(null);
    const [savedStartDate, setSavedStartDate] = useState(null);
    const [savedEndDate, setSavedEndDate] = useState(null);
    const [savedMetrics, setSavedMetrics] = useState(null);
    const [savedDateRangeType, setSavedDateRangeType] = useState(null);
    const [savedAdaptiveRangeValue, setSavedAdaptiveRangeValue] = useState(null);
    const [savedAdaptiveRangeUnit, setSavedAdaptiveRangeUnit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingName, setSavingName] = useState(false);
    const [savingDescription, setSavingDescription] = useState(false);

    useEffect(() => {
        if (savedReportId) {
            setLoading(true);
            axios.get(`/web/ui/report/teacher/activity/activitySummary/${savedReportId}`)
                .then(response => {
                    const data = response.data;

                    if (data.reportName) setReportName(data.reportName);
                    if (data.classAverages) setClassAverages(data.classAverages);
                    if (data.className) setClassName(data.className);
                    if (data.startDate) setSavedStartDate(data.startDate);
                    if (data.endDate) setSavedEndDate(data.endDate);
                    if (data.description) setReportDescription(data.description);
                    if (data.selectedMetrics) setSavedMetrics(data.selectedMetrics);
                    if (data.dateRangeType) setSavedDateRangeType(data.dateRangeType);
                    if (data.adaptiveRangeValue) setSavedAdaptiveRangeValue(data.adaptiveRangeValue);
                    if (data.adaptiveRangeUnit) setSavedAdaptiveRangeUnit(data.adaptiveRangeUnit);

                    if (data.studentComments && Array.isArray(data.studentComments)) {
                        const transformedData = data.studentComments.map((student, index) => ({
                            key: student.studentId || index,
                            studentName: student.name || student.studentName,
                            studentId: student.studentId,
                            email: student.email,
                            comment: student.comment
                        }));
                        setTableData(transformedData);
                    }
                })
                .catch(error => {
                    console.error('Error fetching saved report:', error);
                    setTableData([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [savedReportId]);

    const handleUpdateReportName = useCallback(async (newName) => {
        if (!savedReportId) return;
        setSavingName(true);
        try {
            await axios.put(`/web/ui/report/teacher/activity/updateActivitySummary/${savedReportId}`, {
                reportName: newName,
                description: reportDescription
            });
            setReportName(newName);
            displayToastMessage('Report name updated', null, 'success');
        } catch (error) {
            console.error('Error updating report name:', error);
            displayToastMessage(error.response?.data?.error || 'Failed to update report name', null, 'error');
        } finally {
            setSavingName(false);
        }
    }, [savedReportId, reportDescription]);

    const handleUpdateDescription = useCallback(async (newDescription) => {
        if (!savedReportId) return;
        setSavingDescription(true);
        try {
            await axios.put(`/web/ui/report/teacher/activity/updateActivitySummary/${savedReportId}`, {
                reportName: reportName,
                description: newDescription
            });
            setReportDescription(newDescription);
            displayToastMessage('Description updated', null, 'success');
        } catch (error) {
            console.error('Error updating description:', error);
            displayToastMessage(error.response?.data?.error || 'Failed to update description', null, 'error');
        } finally {
            setSavingDescription(false);
        }
    }, [savedReportId, reportName]);

    return {
        reportName,
        reportDescription,
        savedStartDate,
        savedEndDate,
        savedMetrics,
        savedDateRangeType,
        savedAdaptiveRangeValue,
        savedAdaptiveRangeUnit,
        loading,
        savingName,
        savingDescription,
        handleUpdateReportName,
        handleUpdateDescription
    };
};

export default useSavedReport;
